export const ruleValidation = (ruleName, rawText, getAllValidProperties, comparisonAllowedProps, stringOnlyFields) => {
  let isValid = true;
  let newValidationErrors = {
    ruleName: '',
    ruleExpression: '',
  };

  if (!ruleName || ruleName?.trim() === '') {
    newValidationErrors.ruleName = 'Rule name is required';
    isValid = false;
  } else {
    const regex = /^[^{}[\]$^<>]+$/;
    if (!regex.test(ruleName)) {
      newValidationErrors.ruleName = '{}[]$^<> are not allowed in rule name';
      isValid = false;
    } else if (ruleName.length > 100) {
      newValidationErrors.ruleName = 'Rule name must be at most 100 characters';
      isValid = false;
    }
  }

  if (!rawText || rawText?.trim() === '') {
    newValidationErrors.ruleExpression = 'Rule expression is required';
    isValid = false;
  } else {
    const validProperties = getAllValidProperties();
    const ruleParts = rawText?.split(/\s+(AND|OR)\s+/);
    const validLogicalOps = ['AND', 'OR'];
    const numericProps = [':amount:'];
    const allowedCurrencies = ['PKR', 'OMR', 'AED', 'AUD', 'CAD', 'GBP', 'SAR', 'USD', 'EUR', 'KWD', 'BHD', 'QAR', 'SGD', 'CNY', 'GBP'];
    const allowedCardBrands = ['VISA', 'MASTERCARD', 'AMEX'];
    const countryFields = [':ip_address_country:', ':billing_address_country:', ':shipping_address_country:', ':card_country:'];

    const logicalOpsCount = (rawText?.match(/\b(AND|OR)\b/g) || [])?.length;
    if (logicalOpsCount > 2) {
      newValidationErrors.ruleExpression = `A maximum of 2 logical operators are allowed, found: ${logicalOpsCount}`;
      isValid = false;
      return { isValid, newValidationErrors };
    }
    const velocityCount = (rawText?.match(/\bvelocity\(/g) || [])?.length;
    const relativeVelocityCount = (rawText?.match(/\brelative_velocity\(/g) || [])?.length;
    if (velocityCount + relativeVelocityCount > 1) {
      newValidationErrors.ruleExpression = `A maximum of 1 velocity or relative velocity condition is allowed.`;
      isValid = false;
      return { isValid, newValidationErrors };
    }

    for (let i = 0; i < ruleParts?.length; i++) {
      const part = ruleParts[i]?.trim();

      if (i % 2 === 1) {
        if (!validLogicalOps?.includes(part)) {
          newValidationErrors.ruleExpression = `Invalid logical operator.`;
          isValid = false;
          break;
        }
      } else {
        const multipleOperatorsPattern = /[<>=!]{2,}/g;
        if ((part?.match(multipleOperatorsPattern) || [])?.length > 1) {
          newValidationErrors.ruleExpression = `Invalid or incomplete rule.`;
          isValid = false;
          break;
        }
        const propToPropPattern = /^(:\w+?:)\s*(=|!=)\s*(:\w+?:)$/;
        const propToPropMatch = part?.match(propToPropPattern);

        if (propToPropMatch) {
          const left = propToPropMatch[1];
          const operator = propToPropMatch[2];
          const right = propToPropMatch[3];

          if (!comparisonAllowedProps?.includes(left) || !comparisonAllowedProps?.includes(right)) {
            newValidationErrors.ruleExpression = `These properties can't be compared with each other.`;
            isValid = false;
            break;
          }

          if (left === right) {
            newValidationErrors.ruleExpression = `Both sides of the comparison must be different.`;
            isValid = false;
            break;
          }

          if (!['=', '!=']?.includes(operator)) {
            newValidationErrors.ruleExpression = `Only '=' or '!=' allowed for property-to-property comparison.`;
            isValid = false;
            break;
          }

          continue;
        }

        const pattern = /^(.+?)\s*(>=|<=|!=|=|<|>)\s*(('([^']*)')|("([^"]*)")|\d+|true|false)$/;
        const match = part?.match(pattern);
        if (!match) {
          newValidationErrors.ruleExpression = `Invalid or incomplete rule.`;
          isValid = false;
          break;
        }
        const left = match[1]?.trim();
        const operator = match[2];
        const rawValue = match[3]?.trim();
        const value = rawValue?.replace(/^['"]|['"]$/g, '');

        if (left === ':authentication_not_in_effect:') {
          if (!['=', '!=']?.includes(operator)) {
            newValidationErrors.ruleExpression = `Only '=' or '!=' allowed for ":authentication_not_in_effect:"`;
            isValid = false;
            break;
          }
          if (!['true', 'false']?.includes(value)) {
            newValidationErrors.ruleExpression = `Value for ":authentication_not_in_effect:" must be either true or false.`;
            isValid = false;
            break;
          }
          if (/^['"].*['"]$/.test(rawValue)) {
            newValidationErrors.ruleExpression = `Value for ":authentication_not_in_effect:" should not be quoted.`;
            isValid = false;
            break;
          }
        }

        if (left?.startsWith('velocity(') || left?.startsWith('relative_velocity(')) {
          if (!isValidVelocityStructure(left)) {
            newValidationErrors.ruleExpression = `Invalid velocity structure.`;
            isValid = false;
            break;
          }
          if (isNaN(Number(value)) || Number(value) <= 0 || /^['"].*['"]$/.test(rawValue)) {
            newValidationErrors.ruleExpression = `Velocity rule must compare to a number > 0 and not be a string.`;
            isValid = false;
            break;
          }
        } else {
          if (!validProperties?.includes(left)) {
            newValidationErrors.ruleExpression = `Invalid property or structure`;
            isValid = false;
            break;
          }
          if (numericProps?.includes(left)) {
            if (isNaN(Number(value)) || Number(value) <= 0 || /^['"].*['"]$/.test(rawValue)) {
              newValidationErrors.ruleExpression = `:amount: must be a number > 0 and not be a string.`;
              isValid = false;
              break;
            }
          }
          if (stringOnlyFields?.includes(left)) {
            if (!['=', '!=']?.includes(operator)) {
              newValidationErrors.ruleExpression = `Only '=' or '!=' allowed for "${left}"`;
              isValid = false;
              break;
            }
            if (!/^(['"]).*\1$/.test(rawValue)) {
              newValidationErrors.ruleExpression = `Value for "${left}" must be a quoted string with matching quotes.`;
              isValid = false;
              break;
            }
            if (left === ':currency:' && !allowedCurrencies?.includes(value)) {
              newValidationErrors.ruleExpression = `":currency:" must be one of: ${allowedCurrencies?.join(', ')}`;
              isValid = false;
              break;
            }

            if (left === ':card_brand:' && !allowedCardBrands?.includes(value)) {
              newValidationErrors.ruleExpression = `":card_brand:" must be one of: ${allowedCardBrands?.join(', ')}`;
              isValid = false;
              break;
            }

            if (left === ':ip_address:') {
              const ipPattern = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
              if (!ipPattern.test(value)) {
                newValidationErrors.ruleExpression = `":ip_address:" must be a valid IP address.`;
                isValid = false;
                break;
              }
            }

            if (countryFields.includes(left)) {
              const countryNameRegex = /^[a-zA-ZÀ-ÿ' -]+$/;
              if (!countryNameRegex.test(value)) {
                newValidationErrors.ruleExpression = `"${left}" must be a valid country name (letters, spaces, hyphens, apostrophes allowed).`;
                isValid = false;
                break;
              }
            }

            if (left === ':bin:') {
              if (!(value.length === 6 || value.length === 8)) {
                newValidationErrors.ruleExpression = `":bin:" must be 6 or 8 characters long: "${value}"`;
                isValid = false;
                break;
              }
              if (!/^\d+$/.test(value)) {
                newValidationErrors.ruleExpression = `":bin:" must contain only digits: "${value}"`;
                isValid = false;
                break;
              }
            }
          }
        }
      }
    }
  }

  return { isValid, newValidationErrors };
};

const isValidVelocityStructure = (str) => {
  const validTransactionStatuses = ['declined', 'approved', 'attempted'];
  const transactionalFields = ['ip_address', 'billing_address', 'shipping_address', 'email_address', 'card', 'card_country', 'bin', 'ip_address_country'];
  const relativeFields = ['ip_address', 'billing_address', 'email_address', 'card', 'card_country', 'bin', 'ip_address_country', 'phone_number' /* 'email_domain' */];
  const velocityRegex = /^velocity\(([^,]+),\s*([^,]+),\s*([^)]+)\)$/;
  const relativeVelocityRegex = /^relative_velocity\(([^,]+),\s*([^,]+),\s*([^)]+)\)$/;
  const timePeriodRegex = /^(\d+)([mhd])$/i;
  const validateTimePeriod = (timePeriod) => {
    const match = timePeriod?.match(timePeriodRegex);
    if (!match) return false;
    const number = parseInt(match[1], 10);
    const unit = match[2]?.toLowerCase();
    if (number <= 0) return false;
    if (!['m', 'h', 'd']?.includes(unit)) return false;
    return true;
  };

  const velocityMatch = str?.match(velocityRegex);
  const relativeVelocityMatch = str?.match(relativeVelocityRegex);
  if (velocityMatch) {
    const field = velocityMatch[1]?.trim();
    const timePeriod = velocityMatch[2]?.trim();
    const status = velocityMatch[3]?.trim();

    if (!transactionalFields?.includes(field)) {
      return false;
    }
    if (!validateTimePeriod(timePeriod)) {
      return false;
    }
    if (!validTransactionStatuses?.includes(status)) {
      return false;
    }
    return true;
  }

  if (relativeVelocityMatch) {
    const field = relativeVelocityMatch[1]?.trim();
    const timePeriod = relativeVelocityMatch[2]?.trim();
    const status = relativeVelocityMatch[3]?.trim();

    if (!validateTimePeriod(timePeriod)) {
      return false;
    }
    if (!validTransactionStatuses?.includes(status)) {
      return false;
    }

    const parts = field?.split('_per_');
    if (parts?.length !== 2) {
      return false;
    }

    const [first, second] = parts;
    if (!relativeFields?.includes(first) || !relativeFields?.includes(second)) {
      return false;
    }

    if (first === second) {
      return false;
    }
    return true;
  }
  return false;
};
